import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  DEFAULT_SETTINGS,
  nowIso,
  type AnnotationRecord,
  type DocumentRecord,
  type PdfBlobRecord,
  type ReadingStateRecord,
  type SettingsRecord,
} from "@/domain";
import {
  buildDocumentExport,
  buildLibraryExport,
  downloadExport,
  downloadLibraryExport,
  importMarginaliaBackup,
} from "@/interchange";
import { identifyPdfFile } from "@/documents/document-identity";
import { LibraryScreen } from "@/documents/LibraryScreen";
import type { LibraryDocumentView } from "@/documents/DocumentRow";
import { PasswordDialog } from "@/documents/PasswordDialog";
import {
  PdfLoadManager,
  type PdfDocumentSession,
} from "@/pdf/load-pdf";
import type {
  PdfPasswordProvider,
  PdfPasswordRequest,
} from "@/pdf/password-controller";
import type { NewAnnotationInput } from "@/reader/ReaderScreen";
import { SettingsScreen } from "@/app/SettingsScreen";
import {
  assessPdfRetention,
  createLocalDataServices,
  estimateStorage,
  requestPersistentStorage,
} from "@/storage";

const APP_VERSION = "0.1.0";
const services = createLocalDataServices();
const pdfLoadManager = new PdfLoadManager();
const ReaderScreen = lazy(async () => ({
  default: (await import("@/reader/ReaderScreen")).ReaderScreen,
}));

type CurrentDocumentSession = {
  document: DocumentRecord;
  pdf: PdfDocumentSession;
  annotations: AnnotationRecord[];
  readingState: ReadingStateRecord | null;
};

type Notice = {
  tone: "info" | "success" | "warning" | "error";
  message: string;
};

type PasswordDialogRequest = PdfPasswordRequest & {
  fileName: string;
};

function metadataString(
  info: Record<string, unknown> | undefined,
  key: string,
): string | null {
  const value = info?.[key];
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function toLibraryDocument(document: DocumentRecord): LibraryDocumentView {
  return {
    id: document.id,
    fileName: document.fileName,
    title: document.title,
    author: document.author,
    byteLength: document.byteLength,
    pageCount: document.pageCount,
    pdfAvailability: document.pdfAvailability,
    lastOpenedAt: document.lastOpenedAt,
  };
}

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return "Marginalia could not complete that action.";
}

export function AppController() {
  const [screen, setScreen] = useState<"library" | "reader" | "settings">(
    "library",
  );
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [annotationCount, setAnnotationCount] = useState(0);
  const [settings, setSettings] =
    useState<SettingsRecord>(DEFAULT_SETTINGS);
  const [current, setCurrent] = useState<CurrentDocumentSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [storage, setStorage] = useState<{
    usage: number;
    quota: number;
    persisted: boolean | null;
  } | null>(null);
  const [passwordRequest, setPasswordRequest] =
    useState<PasswordDialogRequest | null>(null);
  const passwordResolver = useRef<((password: string | null) => void) | null>(
    null,
  );
  const currentDocumentId = useRef<string | null>(null);
  const readingStateTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingReadingState = useRef<ReadingStateRecord | null>(null);

  const refreshStorage = useCallback(async () => {
    const estimate = await estimateStorage();
    if (estimate.usage === null || estimate.quota === null) {
      setStorage(null);
      return;
    }
    let persisted: boolean | null = null;
    try {
      persisted = navigator.storage?.persisted
        ? await navigator.storage.persisted()
        : null;
    } catch {
      persisted = null;
    }
    setStorage({
      usage: estimate.usage,
      quota: estimate.quota,
      persisted,
    });
  }, []);

  const refreshLibrary = useCallback(async () => {
    const [nextDocuments, nextSettings, nextAnnotationCount] = await Promise.all([
      services.documents.listRecent(),
      services.settings.get(),
      services.db.annotations
        .filter((annotation) => annotation.deletedAt === null)
        .count(),
    ]);
    setDocuments(nextDocuments);
    setSettings(nextSettings);
    setAnnotationCount(nextAnnotationCount);
    await refreshStorage();
  }, [refreshStorage]);

  const refreshCurrentAnnotations = useCallback(async (documentId: string) => {
    const annotations =
      await services.annotations.listForDocument(documentId);
    setCurrent((value) =>
      value?.document.id === documentId
        ? { ...value, annotations }
        : value,
    );
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await services.db.open();
        if (cancelled) return;
        await refreshLibrary();
      } catch (error) {
        if (!cancelled) {
          setNotice({ tone: "error", message: errorMessage(error) });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    const unsubscribe = services.invalidation?.subscribe((message) => {
      void refreshLibrary();
      if (currentDocumentId.current !== message.documentId) return;

      if (message.type === "document-deleted") {
        if (readingStateTimer.current) {
          clearTimeout(readingStateTimer.current);
          readingStateTimer.current = null;
        }
        pendingReadingState.current = null;
        currentDocumentId.current = null;
        void pdfLoadManager.close().finally(() => {
          setCurrent(null);
          setScreen("library");
          setNotice({
            tone: "warning",
            message:
              "This document was deleted in another tab, so the reader was closed.",
          });
        });
        return;
      }

      void refreshCurrentAnnotations(message.documentId);
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [refreshCurrentAnnotations, refreshLibrary]);

  useEffect(() => {
    currentDocumentId.current = current?.document.id ?? null;
  }, [current?.document.id]);

  const passwordProviderFor = useCallback(
    (fileName: string): PdfPasswordProvider =>
      (request) =>
        new Promise<string | null>((resolve) => {
          passwordResolver.current?.(null);
          passwordResolver.current = resolve;
          setPasswordRequest({ ...request, fileName });
        }),
    [],
  );

  const finishOpening = useCallback(
    async (
      document: DocumentRecord,
      pdf: PdfDocumentSession,
    ): Promise<void> => {
      const [annotations, readingState] = await Promise.all([
        services.annotations.listForDocument(document.id),
        services.readingStates.get(document.id),
      ]);
      const updated: DocumentRecord = {
        ...document,
        lastOpenedAt: nowIso(),
        updatedAt: nowIso(),
      };
      await services.documents.put(updated);
      setCurrent({
        document: updated,
        pdf,
        annotations,
        readingState: readingState ?? null,
      });
      setScreen("reader");
      requestAnimationFrame(() =>
        globalThis.document
          .querySelector<HTMLElement>(".reader-viewport")
          ?.focus(),
      );
    },
    [],
  );

  const loadPdfBytes = useCallback(
    async (bytes: Uint8Array, fileName: string) =>
      pdfLoadManager.open({
        data: bytes,
        passwordProvider: passwordProviderFor(fileName),
      }),
    [passwordProviderFor],
  );

  const mayRetainPdf = useCallback(async (byteLength: number) => {
    const assessment = assessPdfRetention(
      byteLength,
      await estimateStorage(),
    );
    if (!assessment.canLikelyStore) {
      setNotice({
        tone: "warning",
        message:
          "There is not enough estimated browser storage to retain this PDF. It will still open, and your notes will still be saved.",
      });
      return false;
    }
    if (
      assessment.shouldWarn &&
      !window.confirm(
        "Keeping this PDF may use nearly all remaining browser storage. Keep a local copy anyway?",
      )
    ) {
      setNotice({
        tone: "warning",
        message:
          "The PDF will not be retained. You can locate the exact file again later; notes will still be saved.",
      });
      return false;
    }
    return true;
  }, []);

  const openNewOrKnownFile = useCallback(
    async (file: File, keepPdf: boolean) => {
      setBusy(true);
      setNotice({
        tone: "info",
        message: `Reading and identifying ${file.name} locally…`,
      });

      try {
        const identified = await identifyPdfFile(file);
        const retainPdf = keepPdf && (await mayRetainPdf(file.size));
        let record = await services.documents.findBySha256(identified.sha256);
        const pdf = await loadPdfBytes(identified.bytes, file.name);
        const rawMetadata = await pdf.document.getMetadata().catch(() => null);
        const info = rawMetadata?.info as Record<string, unknown> | undefined;
        const timestamp = nowIso();

        if (record) {
          record = {
            ...record,
            fileName: file.name,
            title: metadataString(info, "Title") ?? record.title,
            author: metadataString(info, "Author") ?? record.author,
            byteLength: file.size,
            pageCount: pdf.document.numPages,
            pdfFingerprint: pdf.document.fingerprints[0] ?? null,
            lastOpenedAt: timestamp,
            updatedAt: timestamp,
          };
          await services.documents.put(record);

          if (retainPdf && record.pdfAvailability !== "stored") {
            try {
              record = await services.localData.retainPdf(
                {
                  schemaVersion: 1,
                  documentId: record.id,
                  blob: file,
                  contentType: "application/pdf",
                  byteLength: file.size,
                  storedAt: timestamp,
                },
                timestamp,
              );
              void requestPersistentStorage();
            } catch {
              setNotice({
                tone: "warning",
                message:
                  "The PDF opened, but the browser could not retain a local copy. Notes will still be saved.",
              });
            }
          }
        } else {
          const id = crypto.randomUUID();
          const baseRecord: DocumentRecord = {
            schemaVersion: 1,
            id,
            sha256: identified.sha256,
            pdfFingerprint: pdf.document.fingerprints[0] ?? null,
            fileName: file.name,
            title: metadataString(info, "Title"),
            author: metadataString(info, "Author"),
            byteLength: file.size,
            pageCount: pdf.document.numPages,
            pdfAvailability: "external",
            createdAt: timestamp,
            lastOpenedAt: timestamp,
            updatedAt: timestamp,
          };
          const pdfBlob: PdfBlobRecord | null = retainPdf
            ? {
                schemaVersion: 1,
                documentId: id,
                blob: file,
                contentType: "application/pdf",
                byteLength: file.size,
                storedAt: timestamp,
              }
            : null;
          const created = await services.localData.createDocumentWithBestEffortPdf(
            baseRecord,
            pdfBlob,
          );
          if (!created.ok) throw created.error;
          record = created.value.document;
          if (created.value.retentionError) {
            setNotice({
              tone: "warning",
              message:
                "The PDF opened, but the browser could not retain a local copy. Notes will still be saved.",
            });
          } else if (created.value.pdfStored) {
            void requestPersistentStorage();
          }
        }

        await refreshLibrary();
        await finishOpening(record, pdf);
      } catch (error) {
        setNotice({ tone: "error", message: errorMessage(error) });
      } finally {
        setBusy(false);
      }
    },
    [finishOpening, loadPdfBytes, mayRetainPdf, refreshLibrary],
  );

  const openStoredDocument = useCallback(
    async (item: LibraryDocumentView) => {
      setBusy(true);
      try {
        const [record, stored] = await Promise.all([
          services.documents.get(item.id),
          services.pdfBlobs.get(item.id),
        ]);
        if (!record || !stored) {
          if (record) {
            await services.documents.put({
              ...record,
              pdfAvailability: "external",
              updatedAt: nowIso(),
            });
          }
          setNotice({
            tone: "warning",
            message: "The retained PDF is unavailable. Locate the file again.",
          });
          await refreshLibrary();
          return;
        }
        const pdf = await loadPdfBytes(
          new Uint8Array(await stored.blob.arrayBuffer()),
          record.fileName,
        );
        await finishOpening(record, pdf);
      } catch (error) {
        setNotice({ tone: "error", message: errorMessage(error) });
      } finally {
        setBusy(false);
      }
    },
    [finishOpening, loadPdfBytes, refreshLibrary],
  );

  const locateDocument = useCallback(
    async (item: LibraryDocumentView, file: File) => {
      setBusy(true);
      try {
        const record = await services.documents.get(item.id);
        if (!record) throw new Error("That local document no longer exists.");
        const identified = await identifyPdfFile(file);
        if (identified.sha256 !== record.sha256) {
          setNotice({
            tone: "error",
            message:
              "That file is different from the saved PDF. No notes were attached.",
          });
          return;
        }
        const pdf = await loadPdfBytes(identified.bytes, file.name);
        let updated: DocumentRecord = {
          ...record,
          fileName: file.name,
          pageCount: pdf.document.numPages,
          pdfFingerprint: pdf.document.fingerprints[0] ?? record.pdfFingerprint,
          lastOpenedAt: nowIso(),
          updatedAt: nowIso(),
        };
        await services.documents.put(updated);
        if (
          settings.value.keepPdfByDefault &&
          updated.pdfAvailability !== "stored" &&
          (await mayRetainPdf(file.size))
        ) {
          try {
            updated = await services.localData.retainPdf(
              {
                schemaVersion: 1,
                documentId: updated.id,
                blob: file,
                contentType: "application/pdf",
                byteLength: file.size,
                storedAt: nowIso(),
              },
              nowIso(),
            );
          } catch {
            setNotice({
              tone: "warning",
              message:
                "The PDF opened, but its local copy could not be retained.",
            });
          }
        }
        await refreshLibrary();
        await finishOpening(updated, pdf);
      } catch (error) {
        setNotice({ tone: "error", message: errorMessage(error) });
      } finally {
        setBusy(false);
      }
    },
    [
      finishOpening,
      loadPdfBytes,
      mayRetainPdf,
      refreshLibrary,
      settings.value.keepPdfByDefault,
    ],
  );

  const flushReadingState = useCallback(async () => {
    if (readingStateTimer.current) {
      clearTimeout(readingStateTimer.current);
      readingStateTimer.current = null;
    }
    const state = pendingReadingState.current;
    pendingReadingState.current = null;
    if (state) await services.readingStates.put(state);
  }, []);

  const scheduleReadingState = useCallback((state: ReadingStateRecord) => {
    pendingReadingState.current = state;
    if (readingStateTimer.current) clearTimeout(readingStateTimer.current);
    readingStateTimer.current = setTimeout(() => {
      void flushReadingState();
    }, 350);
  }, [flushReadingState]);

  useEffect(
    () => () => {
      void flushReadingState();
    },
    [flushReadingState],
  );

  const createAnnotation = useCallback(
    async (input: NewAnnotationInput): Promise<AnnotationRecord> => {
      if (!current) throw new Error("No PDF is open.");
      if (!(await services.documents.get(current.document.id))) {
        throw new Error(
          "This document no longer exists in local storage. Reopen it before adding notes.",
        );
      }
      const timestamp = nowIso();
      const record: AnnotationRecord = {
        schemaVersion: 1,
        id: crypto.randomUUID(),
        documentId: current.document.id,
        pageIndex: input.target.pageIndex,
        kind: input.kind,
        target: input.target,
        body: input.body,
        color: input.color,
        preferredSide: input.preferredSide,
        tags: input.tags ?? [],
        conflictOf: input.conflictOf ?? null,
        revision: 1,
        createdAt: timestamp,
        updatedAt: timestamp,
        deletedAt: null,
      };
      await services.annotations.create(record);
      setCurrent((value) =>
        value
          ? { ...value, annotations: [...value.annotations, record] }
          : value,
      );
      void requestPersistentStorage();
      return record;
    },
    [current],
  );

  const updateNote = useCallback(
    async (
      annotation: AnnotationRecord,
      source: string,
    ): Promise<AnnotationRecord> => {
      const updated = await services.annotations.update(
        {
          ...annotation,
          body: { format: "markdown+typst-v1", source },
          updatedAt: nowIso(),
        },
        annotation.revision,
      );
      setCurrent((value) =>
        value
          ? {
              ...value,
              annotations: value.annotations.map((item) =>
                item.id === updated.id ? updated : item,
              ),
            }
          : value,
      );
      return updated;
    },
    [],
  );

  const reloadAnnotation = useCallback(async (annotationId: string) => {
    const annotation = await services.annotations.get(annotationId);
    if (!annotation || annotation.deletedAt !== null) return null;
    setCurrent((value) =>
      value
        ? {
            ...value,
            annotations: value.annotations.some(
              (item) => item.id === annotation.id,
            )
              ? value.annotations.map((item) =>
                  item.id === annotation.id ? annotation : item,
                )
              : [...value.annotations, annotation],
          }
        : value,
    );
    return annotation;
  }, []);

  const deleteAnnotation = useCallback(
    async (annotation: AnnotationRecord) => {
      await services.annotations.softDelete(
        annotation.id,
        annotation.revision,
        nowIso(),
      );
      setCurrent((value) =>
        value
          ? {
              ...value,
              annotations: value.annotations.filter(
                (item) => item.id !== annotation.id,
              ),
            }
          : value,
      );
    },
    [],
  );

  const exportDocument = useCallback(async (documentId: string) => {
    try {
      const bundle = await buildDocumentExport(services.db, documentId, {
        applicationVersion: APP_VERSION,
      });
      downloadExport(bundle);
      setNotice({
        tone: "success",
        message: "Annotation backup downloaded.",
      });
    } catch (error) {
      setNotice({ tone: "error", message: errorMessage(error) });
    }
  }, []);

  const exportLibrary = useCallback(async () => {
    setBusy(true);
    try {
      const bundle = await buildLibraryExport(services.db, {
        applicationVersion: APP_VERSION,
      });
      downloadLibraryExport(bundle);
      setNotice({
        tone: "success",
        message: "Library backup downloaded as one JSON file.",
      });
    } catch (error) {
      setNotice({ tone: "error", message: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  }, []);

  const importBackup = useCallback(
    async (file: File) => {
      setBusy(true);
      try {
        const result = await importMarginaliaBackup(services.db, file, {
          invalidation: services.invalidation,
          confirm(importPreview) {
            if (importPreview.kind === "library") {
              const { preview } = importPreview;
              const { counts } = preview;
              return window.confirm(
                `Import a library backup with ${preview.documentCount} documents?\n\n` +
                  `${preview.willCreateDocuments} document records will be added; ` +
                  `${preview.pdfNeededDocuments} will need their matching PDF.\n\n` +
                  `${counts.created} new annotations, ${counts.updated} updated, ` +
                  `${counts.skipped} unchanged, ${counts.conflicts} conflict copies, ` +
                  `${counts.deletions} deletions.`,
              );
            }
            const { preview } = importPreview;
            const { counts } = preview;
            return window.confirm(
              `Import notes for ${preview.documentFileName}?\n\n` +
                `${counts.created} new, ${counts.updated} updated, ` +
                `${counts.skipped} unchanged, ${counts.conflicts} conflict copies, ` +
                `${counts.deletions} deletions.`,
            );
          },
        });
        if (result.status === "imported") {
          await refreshLibrary();
          const imported = result.result;
          setNotice({
            tone: "success",
            message:
              imported.kind === "library"
                ? imported.result.pdfNeededDocuments > 0
                  ? `Library imported. ${imported.result.pdfNeededDocuments} documents need their matching PDF files.`
                  : "Library backup imported."
                : imported.result.pdfNeeded
                  ? "Notes imported. Locate the matching PDF when you are ready to read."
                  : "Notes imported into the local document.",
          });
        }
      } catch (error) {
        setNotice({ tone: "error", message: errorMessage(error) });
      } finally {
        setBusy(false);
      }
    },
    [refreshLibrary],
  );

  const deleteDocument = useCallback(
    async (item: LibraryDocumentView) => {
      if (
        !window.confirm(
          `Delete ${item.title || item.fileName} and every local note for it?`,
        )
      ) {
        return;
      }
      setBusy(true);
      try {
        if (current?.document.id === item.id) {
          await flushReadingState();
          await pdfLoadManager.close();
          setCurrent(null);
          setScreen("library");
        }
        await services.localData.deleteDocument(item.id);
        await refreshLibrary();
        setNotice({ tone: "success", message: "Local document deleted." });
      } catch (error) {
        setNotice({ tone: "error", message: errorMessage(error) });
      } finally {
        setBusy(false);
      }
    },
    [current?.document.id, flushReadingState, refreshLibrary],
  );

  const removeLocalPdfCopy = useCallback(
    async (item: LibraryDocumentView) => {
      setBusy(true);
      try {
        await services.localData.removeRetainedPdf(item.id, nowIso());
        await refreshLibrary();
        setNotice({
          tone: "success",
          message:
            "The local PDF copy was removed. Notes remain, and the exact file can be located later.",
        });
      } catch (error) {
        setNotice({ tone: "error", message: errorMessage(error) });
      } finally {
        setBusy(false);
      }
    },
    [refreshLibrary],
  );

  const saveSettings = useCallback(
    async (keepPdfByDefault: boolean) => {
      const next: SettingsRecord = {
        ...settings,
        value: { ...settings.value, keepPdfByDefault },
      };
      setSettings(next);
      try {
        await services.settings.put(next);
      } catch (error) {
        setNotice({ tone: "error", message: errorMessage(error) });
      }
    },
    [settings],
  );

  if (screen === "settings") {
    return (
      <SettingsScreen
        keepPdfByDefault={settings.value.keepPdfByDefault}
        documentCount={documents.length}
        annotationCount={annotationCount}
        storage={storage}
        busy={busy}
        onChangeKeepPdf={saveSettings}
        onExportLibrary={() => void exportLibrary()}
        onClearAll={() => {
          if (
            !window.confirm(
              "Permanently delete every local PDF, note, highlight, and setting stored by Marginalia in this browser?",
            )
          ) {
            return;
          }
          void services.db.delete().then(() => globalThis.location.reload());
        }}
        onBack={() => setScreen("library")}
      />
    );
  }

  if (screen === "reader" && current) {
    return (
      <>
        <Suspense
          fallback={
            <main className="reader-shell">
              <div className="reader-loading">
                <strong>Preparing the reading desk…</strong>
                <span>The PDF remains on this device.</span>
              </div>
            </main>
          }
        >
          <ReaderScreen
            document={current.document}
            session={current.pdf}
            annotations={current.annotations}
            initialReadingState={current.readingState}
            highlightColor={settings.value.highlightColor}
            onBack={() => {
              void (async () => {
                await flushReadingState();
                await pdfLoadManager.close();
                setCurrent(null);
                setScreen("library");
                await refreshLibrary();
              })();
            }}
            onExport={() => void exportDocument(current.document.id)}
            onCreateAnnotation={createAnnotation}
            onUpdateNote={updateNote}
            onReloadAnnotation={reloadAnnotation}
            onDeleteAnnotation={deleteAnnotation}
            onReadingStateChange={scheduleReadingState}
          />
        </Suspense>
        <PasswordDialog
          request={passwordRequest}
          onSubmit={(password) => {
            passwordResolver.current?.(password);
            passwordResolver.current = null;
            setPasswordRequest(null);
          }}
          onCancel={() => {
            passwordResolver.current?.(null);
            passwordResolver.current = null;
            setPasswordRequest(null);
          }}
        />
      </>
    );
  }

  return (
    <>
      <LibraryScreen
        documents={documents.map(toLibraryDocument)}
        loading={loading}
        busy={busy}
        keepPdfByDefault={settings.value.keepPdfByDefault}
        notice={notice}
        onOpenFile={(file, keepPdf) =>
          void openNewOrKnownFile(file, keepPdf)
        }
        onOpenStored={(item) => void openStoredDocument(item)}
        onLocate={(item, file) => void locateDocument(item, file)}
        onExport={(item) => void exportDocument(item.id)}
        onRemoveLocalCopy={(item) => void removeLocalPdfCopy(item)}
        onImport={(file) => void importBackup(file)}
        onDelete={(item) => void deleteDocument(item)}
      />
      <PasswordDialog
        request={passwordRequest}
        onSubmit={(password) => {
          passwordResolver.current?.(password);
          passwordResolver.current = null;
          setPasswordRequest(null);
        }}
        onCancel={() => {
          passwordResolver.current?.(null);
          passwordResolver.current = null;
          setPasswordRequest(null);
        }}
      />
    </>
  );
}
